import React from 'react';

interface PrivacyPolicyProps {
  onBack: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-[#1E293B] text-white p-4 sm:p-6 md:p-8 font-sans" dir="rtl">
      <div className="max-w-3xl mx-auto">
        {/* Navigation Bar */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-700/60">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-emerald-400 font-medium transition-colors border border-slate-700"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 18 6-6-6-6"/>
            </svg>
            <span>العودة للرئيسية</span>
          </button>
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
            className="text-xs text-slate-400 hover:text-emerald-400 transition-colors"
          >
            موقع ابن سيرين لتفسير الأحلام
          </a>
        </div>

        {/* Header */}
        <header className="mb-8">
          <div className="inline-block px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold rounded-full mb-3">
            وثيقة الخصوصية وسرية البيانات
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">سياسة الخصوصية وسرية الرؤى والأحلام</h1>
          <p className="text-sm text-slate-400">آخر تحديث: مارس 2025</p>
        </header>

        {/* Content Cards */}
        <div className="space-y-6 text-slate-300 leading-relaxed">
          {/* Section 1 */}
          <section className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2">
              <span>١. التزامنا بالأمانة والسرية التامة</span>
            </h2>
            <p className="text-slate-200 mb-3">
              نحن في تطبيق وموقع "ابن سيرين" لتفسير الأحلام ندرك أن الرؤى والأحلام من أخص أسرار الإنسان، وانطلاقاً من الأدب الإسلامي في تعبير الرؤيا الذي يوجب كتمان السر والنصح للرائي، نلتزم بحماية خصوصية كافة البيانات والرؤى التي تدخلها أعلى درجات الحماية.
            </p>
          </section>

          {/* Section 2 */}
          <section className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2">
              <span>٢. البيانات التي نطلبها</span>
            </h2>
            <p className="text-slate-200 mb-2">
              لتقديم تأويل دقيق ومخصص يراعي السياق الخاص بك كما نص أئمة التعبير (ابن سيرين والنابلسي)، قد يطلب النظام إدخال:
            </p>
            <ul className="list-disc list-inside space-y-1.5 text-slate-300 mr-2">
              <li>تفاصيل الرؤيا أو الحلم بدقة.</li>
              <li>العمر، والجنس، والحالة الاجتماعية (أعزب، متزوج، إلخ).</li>
              <li>وقت الرؤيا والحالة النفسية قبل النوم (اختياري).</li>
            </ul>
            <p className="mt-3 text-xs text-emerald-400/90 font-medium">
              * لا نطلب اسمك الحقيقي، ولا بريدك الإلكتروني، ولا رقم هاتفك، ولا أي مستندات شخصية.
            </p>
          </section>

          {/* Section 3 */}
          <section className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2">
              <span>٣. كيف نستخدم بياناتك</span>
            </h2>
            <p className="text-slate-200">
              تُستخدم البيانات المدخلة حصراً وفورياً لتوليد التعبير الشرعي والرمزي عبر خوارزميات الذكاء الاصطناعي المتخصصة والمدربة على كتب التراث الإسلامي المعتمدة. لا نقوم ببيع أو تأجير أو مشاركة أي من أحلامك مع أي جهات إعلانية أو أطراف ثالثة نهائياً.
            </p>
          </section>

          {/* Section 4 */}
          <section className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2">
              <span>٤. الحماية والتشفير</span>
            </h2>
            <p className="text-slate-200">
              يتم نقل جميع البيانات عبر اتصالات مشفرة بروتوكول طبقة المنافذ الآمنة (HTTPS / TLS)، مما يحول دون اعتراض البيانات أثناء الإرسال.
            </p>
          </section>
        </div>

        {/* Footer CTA */}
        <div className="mt-10 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400">ابن سيرين - مفسر الأحلام الذكي وقاموس الرؤى</p>
          <button
            onClick={onBack}
            className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors shadow-lg shadow-emerald-900/30"
          >
            تفسير حلمك الآن
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
